"use client";

// src/components/upload/upload.tsx
import { useId, useState } from "react";
import { Button } from "../button/button.mjs";
import { UploadPreview } from "./upload-preview.mjs";
import { uploadStyles } from "./upload.styles.mjs";
import { parseFile } from "./upload.utils.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
function Upload({
  name,
  children,
  startContent,
  endContent,
  className,
  error,
  multiple,
  accept = "image/*",
  maxFileSize = 5,
  maxFiles = 5,
  showPreview = true,
  onUpload,
  onRemove,
  disabled = false,
  files: _uploadedFiles = []
}) {
  const [files, setImages] = useState(_uploadedFiles);
  const uid = useId();
  const style = uploadStyles();
  const errorId = `${uid}-error`;
  async function handleUpload(_files) {
    const newFiles = _files.filter(
      (img) => !files.find((previewImg) => previewImg.name === img.name)
    );
    const fileLimitRemaining = Math.abs(maxFiles - files.length);
    const fileLimitReached = multiple && maxFiles && maxFiles <= files.length;
    if (fileLimitReached) {
      return;
    }
    const filesToUpload = newFiles.slice(
      0,
      maxFiles ? fileLimitRemaining : void 0
    );
    const parsedFilesToUpload = filesToUpload.map(
      (file) => parseFile({ file, maxFileSize })
    );
    setImages((prev) => [...prev, ...parsedFilesToUpload]);
    const uploadedFiles = await onUpload(parsedFilesToUpload);
    setImages(
      (prev) => prev.map((currentImage) => {
        const uploadedFile = uploadedFiles.find(
          (_img) => _img.name === currentImage.name
        );
        return !uploadedFile ? currentImage : {
          ...currentImage,
          id: uploadedFile.id ?? uploadedFile.name,
          error: uploadedFile.error,
          pending: false
        };
      })
    );
  }
  const handleChange = (event) => {
    if (!event.target.files) return;
    const filesArray = Array.from(event.target.files);
    handleUpload(filesArray);
  };
  function handleDrop(event) {
    event.stopPropagation();
    event.preventDefault();
    if (event.dataTransfer?.items) {
      const uploadedFiles = Array.from(event.dataTransfer.items).reduce(
        (_files, item) => {
          if (item.kind === "file") {
            const file = item.getAsFile();
            file && _files.push(file);
          }
          return _files;
        },
        []
      );
      handleUpload(uploadedFiles);
      return;
    }
    if (event.dataTransfer?.files) {
      handleUpload(Array.from(event.dataTransfer.files));
      return;
    }
  }
  function handleDragOver(event) {
    event.stopPropagation();
    event.preventDefault();
  }
  function removeImage(id) {
    setImages((prev) => prev.filter((i) => i.id !== id));
    onRemove?.(id);
  }
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsxs(
      Button,
      {
        className: "py-8",
        as: "label",
        variant: "outlineDashed",
        startContent,
        endContent,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
        children: [
          children,
          /* @__PURE__ */ jsx(
            "input",
            {
              id: uid,
              className: "sr-only",
              type: "file",
              name,
              multiple,
              accept,
              onChange: handleChange,
              disabled
            }
          )
        ]
      }
    ),
    showPreview && /* @__PURE__ */ jsx(UploadPreview, { files, removeFile: removeImage }),
    error && /* @__PURE__ */ jsx(
      "span",
      {
        id: errorId,
        role: "alert",
        className: style.error({ className: className?.error }),
        children: error
      }
    )
  ] });
}
export {
  Upload
};
